import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
type TabSlug = 'faq' | 'terms' | 'cookies' | 'privacy' | 'safeguarding' | 'photo-consent';
interface Tab {
  slug: TabSlug;
  label: string;
}
const tabs: Tab[] = [{
  slug: 'faq',
  label: 'FAQ'
}, {
  slug: 'terms',
  label: 'Terms & Conditions'
}, {
  slug: 'cookies',
  label: 'Cookie Policy'
}, {
  slug: 'privacy',
  label: 'Privacy Policy'
}, {
  slug: 'safeguarding',
  label: 'Safeguarding'
}, {
  slug: 'photo-consent',
  label: 'Photo & Video Consent'
}];
export function LegalPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabSlug>(location.state?.tab as TabSlug || 'faq');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab as TabSlug);
    }
  }, [location.state]);
  const renderContent = () => {
    switch (activeTab) {
      case 'faq':
        return <motion.div key="faq" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-923">
            <p className="text-sm text-navy-400 mb-6" data-id="element-924">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-925">
              Frequently Asked Questions
            </h2>

            <div className="space-y-8" data-id="element-926">
              <div data-id="element-927">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-928">
                  What should my child wear for the sessions?
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-929">
                  Your child should wear clothes they are comfortable to move
                  around in and have appropriate footwear for physical activity.
                </p>
              </div>

              <div data-id="element-930">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-931">
                  What will my child need to bring?
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-navy-600" data-id="element-932">
                  <li data-id="element-933">
                    A snack for break-time – please ensure these are nut free.
                  </li>
                  <li data-id="element-934">
                    A refillable water bottle with your child's name on it.
                  </li>
                  <li data-id="element-935">Clothing for sports activities.</li>
                  <li data-id="element-936">
                    Trainers or shoes for general sports, ideally with
                    non-marking soles.
                  </li>
                  <li data-id="element-937">
                    Sunscreen and a hat for hot days and a waterproof jacket for
                    rainy days.
                  </li>
                </ul>
              </div>

              <div data-id="element-938">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-939">
                  What should my child leave at home?
                </h3>
                <p className="text-navy-600 leading-relaxed mb-2" data-id="element-940">
                  <strong data-id="element-941">Mobile phones</strong> – We understand that children
                  use their phones more and more these days. While we're not
                  against them, we encourage that phones are kept in bags and
                  only used for essential contact.
                </p>
                <p className="text-navy-600 leading-relaxed mb-2" data-id="element-942">
                  <strong data-id="element-943">Money</strong> – There won't be any tuck shops.
                </p>
                <p className="text-navy-600 leading-relaxed" data-id="element-944">
                  <strong data-id="element-945">Valuables</strong> – Such as expensive watches and
                  non-essential jewellery.
                </p>
              </div>

              <div data-id="element-946">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-947">
                  What first aid will be provided?
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-948">
                  While your child is in our care, every effort is made to
                  ensure a safe playing environment. However, if an accident
                  does occur, qualified first aiders are always available. At
                  all times the wishes of the parent will be upheld if further
                  medical assistance is required.
                </p>
              </div>

              <div data-id="element-949">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-950">
                  Do you accept childcare vouchers?
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-951">
                  Please contact us directly at info@brightstarsports.com to
                  discuss payment via childcare vouchers for our holiday
                  workshops and weekly classes.
                </p>
              </div>
            </div>
          </motion.div>;
      case 'terms':
        return <motion.div key="terms" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-952">
            <p className="text-sm text-navy-400 mb-6" data-id="element-953">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-954">
              Terms & Conditions
            </h2>

            <div className="space-y-8" data-id="element-955">
              <div data-id="element-956">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-957">
                  Holiday Workshops & Weekly Classes
                </h3>
                <h4 className="font-bold text-navy-900 mb-2 uppercase text-sm tracking-wider" data-id="element-958">
                  General
                </h4>
                <ul className="list-disc pl-6 space-y-3 text-navy-600" data-id="element-959">
                  <li data-id="element-960">
                    Customers must ensure when registering a child their details
                    are accurate and up to date in all respects at the point of
                    booking. Any changes should be sent to Bright Star Sports
                    immediately.
                  </li>
                  <li data-id="element-961">
                    Customers are not required to accompany their children
                    during these sessions or remain at the premises during the
                    class/workshop. However, customers are welcome to view from
                    appropriate viewing galleries or side lines where there is
                    sufficient space.
                  </li>
                  <li data-id="element-962">
                    Customers should aim to arrive no more than 10 minutes
                    before a class/workshop. Customers should drop-off and
                    collect children from the designated area.
                  </li>
                  <li data-id="element-963">
                    Customers must inform Bright Star Sports of any special
                    medical conditions at the point of booking. This includes
                    any special educational needs. It is advised that children
                    requiring (prescription) medication should take it prior to
                    attending as appropriate. For children who need to
                    self-administer medication during a workshop (e.g. an asthma
                    inhaler), the customer should advise the coach of their
                    permission for their child to do this.
                  </li>
                  <li data-id="element-964">
                    The customer or child should not attend whilst suffering
                    with any serious illness or contagious disease.
                  </li>
                  <li data-id="element-965">
                    Customers and children must follow health and safety
                    procedures as advised to them by their coach.
                  </li>
                  <li data-id="element-966">
                    All lead coaches hold up-to-date first aid certificates and
                    are fully DBS checked.
                  </li>
                  <li data-id="element-967">
                    Customers accept that at times there will be physical
                    contact between the coach and their child in a professional,
                    caring manner.
                  </li>
                  <li data-id="element-968">
                    Customers are advised to bring a drink for their child and
                    please feel free to add a healthy snack to their kit bag.
                    Please avoid snacks that contain nuts.
                  </li>
                </ul>
              </div>

              <div data-id="element-969">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-970">
                  Website Terms of Use
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-971">
                  Welcome to our website. If you continue to browse and use this
                  website, you are agreeing to comply with and be bound by the
                  following terms and conditions of use, which together with our
                  privacy policy govern Bright Star Sports' relationship with
                  you in relation to this website.
                </p>
                <ul className="list-disc pl-6 space-y-3 text-navy-600" data-id="element-972">
                  <li data-id="element-973">
                    The content of the pages of this website is for your general
                    information and use only. It is subject to change without
                    notice.
                  </li>
                  <li data-id="element-974">
                    This website uses cookies to monitor browsing preferences.
                    If you do allow cookies to be used, personal information may
                    be stored by us for use by third parties.
                  </li>
                  <li data-id="element-975">
                    Neither we nor any third parties provide any warranty or
                    guarantee as to the accuracy, timeliness, performance,
                    completeness or suitability of the information and materials
                    found or offered on this website for any particular purpose.
                  </li>
                  <li data-id="element-976">
                    Your use of any information or materials on this website is
                    entirely at your own risk, for which we shall not be liable.
                    It shall be your own responsibility to ensure that any
                    products, services or information available through this
                    website meet your specific requirements.
                  </li>
                  <li data-id="element-977">
                    This website contains material which is owned by or licensed
                    to us. This material includes, but is not limited to, the
                    design, layout, look, appearance and graphics. Reproduction
                    is prohibited other than in accordance with the copyright
                    notice, which forms part of these terms and conditions.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>;
      case 'cookies':
        return <motion.div key="cookies" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-978">
            <p className="text-sm text-navy-400 mb-6" data-id="element-979">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-980">
              Cookie Policy
            </h2>

            <div className="space-y-8" data-id="element-981">
              <div data-id="element-982">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-983">
                  Cookies
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-984">
                  We use traffic log cookies when you visit our site. We analyse
                  this data to improve our site and tailor it to our clients'
                  needs.
                </p>
              </div>

              <div data-id="element-985">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-986">
                  What is a cookie?
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-987">
                  A cookie is a small file of letters and numbers placed on your
                  browser or the hard drive of your computer. It enables us to
                  see how many people visit our website and which pages they
                  tend to visit. We use the information cookies collect in order
                  to improve our service. No personally identifiable information
                  is stored on these cookies.
                </p>
              </div>

              <div data-id="element-988">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-989">
                  Your consent
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-990">
                  Unless you have adjusted your browser setting so that it will
                  refuse cookies, our system will issue cookies as soon as you
                  visit our website. By continuing to use our site, you are
                  agreeing to us placing cookies on your computer.
                </p>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-991">
                  Please read this policy carefully for details about the
                  information we collect when you use this site. If you do not
                  wish to accept cookies in relation to this site, you must
                  disable them using your browser settings.
                </p>
                <p className="text-navy-600 leading-relaxed font-bold" data-id="element-992">
                  How we may use the information generated by cookies.
                </p>
                <p className="text-navy-600 leading-relaxed mt-2" data-id="element-993">
                  We analyse our web traffic using Google Analytics. It is the
                  most widely used web analytics service on the internet. It is
                  a free service from Google and provides us with useful
                  information about how many visitors we have to our website,
                  what pages they visit and how long they stay.
                </p>
              </div>
            </div>
          </motion.div>;
      case 'privacy':
        return <motion.div key="privacy" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-994">
            <p className="text-sm text-navy-400 mb-6" data-id="element-995">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-996">
              Privacy Policy
            </h2>

            <div className="space-y-8" data-id="element-997">
              <div data-id="element-998">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-999">
                  Privacy Notice
                </h3>
                <h4 className="font-bold text-navy-900 mb-2 uppercase text-sm tracking-wider" data-id="element-1000">
                  Bright Star Sports and Bright Star Sports Website
                </h4>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1001">
                  <strong data-id="element-1002">Background</strong>
                  <br data-id="element-1003" />
                  Bright Star Sports (BSS) is committed to protecting and
                  respecting your privacy. This privacy notice sets out how BSS
                  uses and protects any personal information we collect or you
                  provide to us about yourself and your child or children while
                  using our services and this website.
                </p>
              </div>

              <div data-id="element-1004">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1005">
                  What We Need
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1006">
                  BSS asks for personal data, and you provide that to us. We
                  take your privacy seriously and will only use personal
                  information about you and your child or children to provide
                  the services you have requested from us and to administer your
                  account.
                </p>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1007">
                  We collect personal data from you to deliver the services and
                  programmes you have requested. This includes you and your
                  child or children's name, address, date of birth, and gender
                  to allow us to provide the requested service.
                </p>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1008">
                  Additionally, with your consent, we may seek permission to
                  collect further details on specific medical and relevant
                  healthcare details and history necessary to ensure the welfare
                  and safety of your child or children.
                </p>
                <p className="text-navy-600 leading-relaxed" data-id="element-1009">
                  When you conduct a transaction on our website, such as
                  submitting an enquiry form, we collect personal information
                  you provide, such as your name, address, and email address.
                  Your personal information will only be used for the specific
                  reasons stated above.
                </p>
              </div>

              <div data-id="element-1010">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1011">
                  Why We Need Your Data
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-1012">
                  We need this personal data to provide you with our services
                  and enter into a contract with you. We will not collect any
                  personal data that is not necessary to provide and oversee the
                  services we offer.
                </p>
              </div>

              <div data-id="element-1013">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1014">
                  What We Do With Your Data
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1015">
                  All the personal data we hold about you and your child or
                  children will be processed by our staff in the United Kingdom.
                  No third parties will have access to your personal data unless
                  there is a legal obligation for us to provide it.
                </p>
                <p className="text-navy-600 leading-relaxed" data-id="element-1016">
                  We take all reasonable steps to ensure that your personal data
                  is processed securely.
                </p>
              </div>

              <div data-id="element-1017">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1018">
                  How Long We Keep Your Data
                </h3>
                <p className="text-navy-600 leading-relaxed" data-id="element-1019">
                  We will generally keep your personal data for a minimum of 2
                  years, after which time it will be destroyed if it is no
                  longer required for the lawful purpose(s) for which it was
                  obtained. If you consent to marketing, any information we use
                  for this purpose will be kept with us until you notify us that
                  you no longer wish to receive this information.
                </p>
              </div>
            </div>
          </motion.div>;
      case 'safeguarding':
        return <motion.div key="safeguarding" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-1020">
            <p className="text-sm text-navy-400 mb-6" data-id="element-1021">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-1022">
              Safeguarding Policy
            </h2>

            <div className="space-y-8" data-id="element-1023">
              <div data-id="element-1024">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1025">
                  Safeguarding Policy Statement
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1026">
                  Bright Star Sports (BSS) acknowledges the duty of care to
                  safeguard and promote the welfare of children and young
                  people. BSS are committed to ensuring safeguarding practice
                  reflects statutory responsibilities, UK government guidance
                  and complies with best practice.
                </p>
              </div>

              <div data-id="element-1027">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1028">
                  The purpose and scope of this policy statement is:
                </h3>
                <ul className="list-disc pl-6 space-y-3 text-navy-600" data-id="element-1029">
                  <li data-id="element-1030">
                    To ensure all children attending multi-sport coaching
                    sessions and workshops with BSS have fun, develop their
                    physical literacy skills and we aim to encourage the start
                    of a lifelong love of enjoying sport and physical activity.
                  </li>
                  <li data-id="element-1031">
                    To provide staff and volunteers, as well as children and
                    young people and their families, with the overarching
                    principles that guide our approach to child protection. This
                    policy applies to anyone working on behalf of or with BSS
                    including paid staff, volunteers and students.
                  </li>
                </ul>
              </div>

              <div data-id="element-1032">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1033">
                  BSS's safeguarding policy recognises that:
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1034">
                  The welfare and interests of children and young people at risk
                  are paramount in all circumstances. It aims to ensure that
                  regardless of age, ability or disability, gender reassignment,
                  race, religion or belief, sex or sexual orientation,
                  socio-economic background, all children:
                </p>
                <ul className="list-disc pl-6 space-y-3 text-navy-600" data-id="element-1035">
                  <li data-id="element-1036">
                    Have a positive and enjoyable experience of multi-sports and
                    physical activity participation at BSS in a safe and
                    inclusive environment.
                  </li>
                  <li data-id="element-1037">
                    Are protected from abuse whilst participating in
                    multi-sports and physical activity at BSS.
                  </li>
                </ul>
              </div>

              <div data-id="element-1038">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1039">
                  Our Commitment
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1040">
                  BSS acknowledges that some children, including disabled
                  children and young people or those from ethnic minority
                  communities, can be particularly vulnerable to abuse and we
                  accept the responsibility to take reasonable and appropriate
                  steps to ensure their welfare.
                </p>
                <p className="text-navy-600 leading-relaxed mb-4 font-bold" data-id="element-1041">
                  As part of our safeguarding policy BSS will:
                </p>
                <ul className="list-disc pl-6 space-y-3 text-navy-600" data-id="element-1042">
                  <li data-id="element-1043">
                    Promote and prioritise the safety and wellbeing of children
                    and young people.
                  </li>
                  <li data-id="element-1044">Value, listen to and respect children.</li>
                  <li data-id="element-1045">
                    Ensure robust safeguarding arrangements and procedures are
                    in operation.
                  </li>
                  <li data-id="element-1046">
                    Adopt safeguarding best practice through our policies,
                    procedures and code of conduct for staff and volunteers.
                  </li>
                  <li data-id="element-1047">
                    Ensure all staff and volunteers are given appropriate
                    learning opportunities to recognise, identify and respond to
                    signs of abuse, neglect and other safeguarding concerns
                    relating to children and young people.
                  </li>
                  <li data-id="element-1048">
                    Ensure appropriate action is taken in the event of
                    incidents/concerns of abuse and support provided to the
                    individual/s who raise or disclose the concern.
                  </li>
                  <li data-id="element-1049">
                    Ensure that confidential, detailed and accurate records of
                    all safeguarding concerns are maintained and securely
                    stored.
                  </li>
                  <li data-id="element-1050">
                    Prevent the employment/deployment of unsuitable individuals
                    by ensuring all necessary checks are made.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>;
      case 'photo-consent':
        return <motion.div key="photo-consent" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-1051">
            <p className="text-sm text-navy-400 mb-6" data-id="element-1052">
              Last updated: January 2025
            </p>
            <h2 className="text-2xl font-display font-bold text-navy-900 mb-6" data-id="element-1053">
              Photo & Video Consent Policy
            </h2>

            <div className="space-y-8" data-id="element-1054">
              <div data-id="element-1055">
                <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-1056">
                  Bright Star Sports and Bright Star Sports website
                </h3>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1057">
                  Bright Star Sports recognises the need to ensure the welfare
                  and safety of all children involved. As part of this
                  commitment we will not permit photographs, video images or
                  other images of children to be taken or used without the
                  consent of the parent/guardian.
                </p>
                <p className="text-navy-600 leading-relaxed mb-4" data-id="element-1058">
                  The business will take steps to ensure that the photographs,
                  images and footage are used solely for the purpose intended,
                  which is the promotion and celebration of Bright Star Sports
                  and the multi-sport coaching. This will include promotion in
                  print, the website and social media channels.
                </p>
                <p className="text-navy-600 leading-relaxed" data-id="element-1059">
                  Full details of how the business uses and protects personal
                  data and your rights in relation to it are available upon
                  request.
                </p>
              </div>
            </div>
          </motion.div>;
      default:
        return null;
    }
  };
  return <main className="bg-white pt-20" data-id="element-1060">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] overflow-hidden bg-brand-blue" data-id="element-1061">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-1062" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-1063"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1064">
          <div className="mb-8" data-id="element-1065">
            <nav className="flex text-sm font-medium text-white/70 mb-8" data-id="element-1066">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-1067">
                Home
              </Link>
              <span className="mx-2" data-id="element-1068">/</span>
              <span className="text-white" data-id="element-1069">Legal & Policies</span>
            </nav>
          </div>

          <div className="max-w-3xl" data-id="element-1070">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1071">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 uppercase" data-id="element-1072">
                Legal & Policies
              </h1>
              <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed" data-id="element-1073">
                Important information about how we operate and protect our
                community.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tab Bar */}
      <section className="bg-white sticky top-[72px] md:top-[80px] z-40 border-b border-navy-100 shadow-sm" data-id="element-1074">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1075">
          <div className="flex overflow-x-auto scrollbar-hide gap-2 md:gap-4 pt-4" data-id="element-1076">
            {tabs.map(tab => {
            const isActive = activeTab === tab.slug;
            return <button key={tab.slug} onClick={() => setActiveTab(tab.slug)} className={`relative px-4 py-3 text-sm md:text-base font-bold rounded-t-lg transition-colors whitespace-nowrap ${isActive ? 'text-brand-blue' : 'text-navy-500 hover:text-navy-900 hover:bg-navy-50'}`} aria-selected={isActive} role="tab" data-id="element-1077">
                  {tab.label}
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-blue rounded-t-full" initial={false} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-1078" />}
                </button>;
          })}
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-12 md:py-20 bg-gray-50 min-h-[60vh]" data-id="element-1079">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1080">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-navy-100 shadow-sm" data-id="element-1081">
            <AnimatePresence mode="wait" data-id="element-1082">{renderContent()}</AnimatePresence>
          </div>
        </div>
      </section>
    </main>;
}